import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, LayoutGrid, MessageSquareOff, FileSpreadsheet, Layers,
  ClipboardList, Activity, AlertTriangle, Crown, Building2, Users,
  GraduationCap, Home as HomeIcon, BarChart3, Check,
} from "lucide-react";
import { DemoDialog } from "../components/site/DemoDialog";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";
import {
  CommandCenterMockup, PccMockup, KpiCard, ProgressBar, MiniArea, MiniBars, HealthDot,
} from "../components/site/Mockups";
import { StakeholderHub, ComparisonTable, FourPillars } from "../components/site/HomeParts";
import { LaunchingSoon } from "../components/site/Shared";
import { Zoomable } from "../components/site/Zoomable";

/* ============ 1. HERO ============ */
const heroLines = ["THE OPERATING SYSTEM", "FOR MODERN SCHOOLS"];

const Hero = () => (
  <section className="relative chrome pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden" data-testid="hero">
    <div className="absolute inset-0 grid-lines opacity-70" />
    <div className="absolute inset-0 radial-glow" />
    <div className="orb orb-indigo animate-drift -top-40 -right-32 h-[520px] w-[520px]" />
    <div className="orb orb-green animate-drift-slow -bottom-40 -left-32 h-[440px] w-[440px]" />
    <div className="orb orb-violet animate-drift -top-24 left-1/3 h-[380px] w-[380px] opacity-40" />

    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="flex flex-wrap items-center justify-center gap-3">
        <LaunchingSoon tone="dark" label="Launching Soon · Preparing for pilot schools" />
        <span className="overline overline-light inline-flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#F1BF26]" /> School Operating System
        </span>
      </motion.div>

      <h1 className="font-display text-4xl sm:text-5xl md:text-[56px] lg:text-[64px] font-semibold tracking-tight mt-8 leading-[1.05] text-white">
        {heroLines.map((line, i) => (
          <span key={i} className="block overflow-hidden">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
              {i === 1 ? <>FOR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F1BF26] to-[#F5B93C]">MODERN SCHOOLS</span></> : line}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
        className="text-white/60 text-base md:text-xl leading-relaxed mt-8 max-w-2xl mx-auto">
        PARTECON connects classroom execution, school operations, communication and intelligence
        into one live operating layer — the connective tissue for how modern schools run.
      </motion.p>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}
        className="flex justify-center mt-10">
        <Link to="/how-it-works" data-testid="hero-how-btn" className="btn-ghost px-8 py-4 text-sm inline-flex items-center justify-center gap-2">
          How It Works <ArrowUpRight size={16} />
        </Link>
      </motion.div>

      {/* Subtle operating-layer strip — pillar nodes pulsing on a connective line */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
        className="mt-20" data-testid="hero-layer-strip">
        <div className="text-[10.5px] font-mono-accent uppercase tracking-[0.24em] text-white/35 mb-6">
          One operating layer · four pillars · three primitives
        </div>
        <div className="relative max-w-3xl mx-auto">
          <div className="absolute inset-x-0 top-[5px] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
          <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["Academic Execution", "Communication", "Intelligence", "School Operations"].map((p, i) => (
              <div key={p} className="flex flex-col items-center gap-3">
                <span className="relative flex h-2.5 w-2.5">
                  <motion.span className="absolute inline-flex h-full w-full rounded-full bg-[#F1BF26]"
                    animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 2.4, delay: i * 0.3, repeat: Infinity }} />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#0B1240] border border-[#F1BF26]" />
                </span>
                <span className="text-[11px] font-medium text-white/70 whitespace-nowrap">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ 2. PROBLEM → THESIS ============ */
const fragmentedTools = [
  { icon: LayoutGrid, t: "ERPs" },
  { icon: MessageSquareOff, t: "Messaging apps" },
  { icon: FileSpreadsheet, t: "Spreadsheets" },
  { icon: Layers, t: "LMS & records" },
];

const ProblemThesis = () => (
  <section className="canvas py-20 md:py-28" data-testid="problem-thesis-section">
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.95fr_1.05fr] gap-14 items-center">
      <Reveal>
        <span className="overline">The problem · The thesis</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-[#1A1D26]">
          Schools run on disconnected systems.<br />
          <span className="text-[#4F46E5]">PARTECON connects them.</span>
        </h2>
        <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5">
          ERPs, messaging apps, spreadsheets, LMS and manual reports scatter information across
          departments. Decisions get made on stale data. PARTECON is the operating layer that runs
          the whole school on one live, governed foundation.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-7">
          {fragmentedTools.map((f) => (
            <div key={f.t} className="rounded-lg bg-white border border-[#ECECE8] px-3 py-2.5 flex items-center gap-2">
              <f.icon className="text-[#9AA1AD] shrink-0" size={15} />
              <span className="text-[12px] text-[#6B7280]">{f.t}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-xl bg-white border border-[#E4E4FB] p-4 shadow-[0_14px_40px_-16px_rgba(99,102,241,0.2)] flex items-start gap-3">
          <span className="h-2 w-2 rounded-full bg-[#22C55E] mt-1.5 animate-pulse-node shrink-0" />
          <p className="text-[14px] md:text-[15px] text-[#1A1D26] leading-relaxed">
            One <span className="text-[#4F46E5] font-semibold">operating layer</span>. Five role experiences.
            Teachers execute · students learn · parents stay connected · principals see · administration operates.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <StakeholderHub />
      </Reveal>
    </div>
  </section>
);

/* ============ 3. THE OPERATING PILLARS ============ */
const PillarsHome = () => (
  <section className="chrome py-20 md:py-32 relative overflow-hidden" data-testid="pillars-section">
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <span className="overline overline-light">The Operating Layer</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-white">
          Four pillars. Three primitives. One operating layer.
        </h2>
        <p className="text-white/55 text-base md:text-lg leading-relaxed mt-5">
          PARTECON isn't a bundle of features. Every daily function of the school runs on four connected
          pillars — held together by three architectural primitives: <span className="text-white">EOC</span>,{" "}
          <span className="text-white">PCC</span> and <span className="text-white">AHI</span>.
        </p>
      </Reveal>
      <div className="mt-12"><FourPillars /></div>
    </div>
  </section>
);

/* ============ 4. PRODUCT EXPERIENCE (tabbed) ============ */
const MockFrame = ({ title, subtitle, icon: Icon, children }) => (
  <div className="rounded-2xl overflow-hidden border border-[#ECECE8] bg-white shadow-[0_30px_80px_-30px_rgba(16,17,23,0.35)]">
    <div className="h-8 bg-[#0E1117] flex items-center gap-1.5 px-3.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#F87171]" /><span className="h-2.5 w-2.5 rounded-full bg-[#F5B93C]" /><span className="h-2.5 w-2.5 rounded-full bg-[#22C55E]" />
    </div>
    <div className="bg-[#F6F5F1] p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="h-8 w-8 rounded-lg bg-[#EEF0FB] flex items-center justify-center"><Icon size={15} className="text-[#6366F1]" /></span>
        <div><div className="text-[13px] font-semibold text-[#1A1D26] font-display leading-none">{title}</div>
          <div className="text-[10px] text-[#6B7280] mt-1">{subtitle}</div></div>
      </div>
      {children}
    </div>
  </div>
);

const AnalyticsMock = () => (
  <div className="space-y-3">
    <div className="grid grid-cols-4 gap-2.5">
      <KpiCard icon={Users} label="Total Teachers" value="40" />
      <KpiCard icon={ClipboardList} label="Avg Attendance" value="94.2%" sub="+2.1%" trend="up" />
      <KpiCard icon={FileSpreadsheet} label="Log Compliance" value="98%" />
      <KpiCard icon={AlertTriangle} label="Open Issues" value="3" sub="+1 vs last" trend="down" />
    </div>
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-2.5">
      <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
        <div className="text-[11px] font-semibold text-[#1A1D26] mb-3">Teacher's AHI · School-wide teaching adherence</div>
        <MiniArea color="#5B7CFA" />
      </div>
      <div className="bg-white rounded-2xl border border-[#ECECE8] p-4 space-y-2.5">
        <div className="text-[11px] font-semibold text-[#1A1D26] mb-1">AHI Breakdown</div>
        {[["Class Start Punctuality", 80, "good"], ["Attendance Submission", 60, "warn"], ["Evaluation Timeliness", 70, "warn"]].map((r) => (
          <div key={r[0]}><div className="flex justify-between text-[9.5px] mb-1"><span className="text-[#6B7280]">{r[0]}</span><span className="font-mono-accent text-[#1A1D26]">{r[1]}%</span></div><ProgressBar value={r[1]} tone={r[2]} /></div>
        ))}
      </div>
    </div>
  </div>
);

const ExamMock = () => (
  <div className="space-y-3">
    <div className="grid grid-cols-4 gap-2.5">
      <KpiCard icon={Check} label="Exam Pass Rate" value="85%" sub="+5% vs cycle" trend="up" />
      <KpiCard icon={AlertTriangle} label="At-Risk Students" value="18" sub="+4 vs month" trend="down" />
      <KpiCard icon={Activity} label="Growth Rate" value="72%" sub="-3% vs exam" trend="down" />
      <KpiCard icon={ClipboardList} label="Re-Exams" value="8" />
    </div>
    <div className="grid lg:grid-cols-2 gap-2.5">
      <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
        <div className="text-[11px] font-semibold text-[#1A1D26] mb-3">Overall School Performance</div>
        <MiniArea color="#6366F1" />
      </div>
      <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
        <div className="text-[11px] font-semibold text-[#1A1D26] mb-3">Class Passing Rate</div>
        <MiniBars />
      </div>
    </div>
  </div>
);

const ClassesMock = () => (
  <div className="grid lg:grid-cols-[1.5fr_1fr] gap-2.5">
    <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold text-[#1A1D26]">On-Going Classes</span>
        <span className="text-[9px] text-[#22C55E] font-mono-accent flex items-center gap-1"><HealthDot /> 48 in session · 12 not started</span>
      </div>
      <div className="space-y-1.5">
        {[0, 1, 2, 3, 4].map((row) => (
          <div key={row} className="flex gap-1.5">
            {Array.from({ length: 14 }).map((_, i) => {
              const rr = (row * 5 + i) % 11;
              const live = row === 3 && i === 7;
              const bg = live ? "#22C55E" : rr === 4 ? "#FDE68A" : rr === 9 ? "#E5E5E0" : "#C7D2FE";
              return <span key={i} className="h-5 flex-1 rounded-md" style={{ background: bg }} />;
            })}
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] font-semibold text-[#1A1D26]">Class 4-A</span>
        <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#16A34A] font-semibold">Live</span>
      </div>
      <div className="text-[10px] text-[#6B7280]">History · Mr. Anil Sharma</div>
      <div className="text-[9px] text-[#9AA1AD] mt-0.5">Started 18 mins ago</div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] text-[#1A1D26]"><span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Attendance captured</div>
        <div className="flex items-center gap-2 text-[10px] text-[#F5B93C]"><span className="h-1.5 w-1.5 rounded-full bg-[#F5B93C]" /> EOC pending</div>
        <div className="flex items-center gap-2 text-[10px] text-[#6B7280]"><span className="h-1.5 w-1.5 rounded-full bg-[#5B7CFA]" /> On-time start</div>
      </div>
    </div>
  </div>
);

const tabs = [
  { key: "command", label: "Command Center", node: <Zoomable label="Command Center"><CommandCenterMockup live /></Zoomable> },
  { key: "classes", label: "Classes", node: <Zoomable label="Classes"><MockFrame title="Classes" subtitle="Live class monitoring & execution" icon={GraduationCap}><ClassesMock /></MockFrame></Zoomable> },
  { key: "analytics", label: "Analytics", node: <Zoomable label="Analytics"><MockFrame title="Analytics" subtitle="Teacher & student operational intelligence" icon={BarChart3}><AnalyticsMock /></MockFrame></Zoomable> },
  { key: "exam", label: "Examination", node: <Zoomable label="Examination"><MockFrame title="Exam Module" subtitle="Performance & outcomes overview" icon={FileSpreadsheet}><ExamMock /></MockFrame></Zoomable> },
  { key: "pcc", label: "Communication", node: <Zoomable label="PARTECON Communication Channel"><PccMockup /></Zoomable> },
];

const ProductExperience = () => {
  const [active, setActive] = useState("command");
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((cur) => {
        const i = tabs.findIndex((t) => t.key === cur);
        return tabs[(i + 1) % tabs.length].key;
      });
    }, 4200);
    return () => clearInterval(id);
  }, [paused]);
  return (
    <section className="chrome py-20 md:py-32 relative overflow-hidden" data-testid="product-experience-section"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="overline overline-light">Product Experience</span>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-white">One design system. Every workflow.</h2>
          <p className="text-white/55 text-base md:text-lg leading-relaxed mt-5">Explore the surfaces schools operate on every day — recreated in the exact PARTECON product language.</p>
        </Reveal>
        <div className="flex flex-wrap justify-center gap-2 mt-10" data-testid="product-tabs">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setActive(t.key)} data-testid={`product-tab-${t.key}`}
              className={`relative overflow-hidden text-[13px] font-medium rounded-lg px-4 py-2 transition-colors ${active === t.key ? "bg-[#6366F1] text-white" : "bg-white/5 text-white/55 hover:text-white border border-white/10"}`}>
              {t.label}
              {active === t.key && !paused && (
                <motion.span className="absolute bottom-0 left-0 h-0.5 bg-white/70" key={active}
                  initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 4.2, ease: "linear" }} />
              )}
            </button>
          ))}
        </div>
        <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8 max-w-5xl mx-auto">
          {tabs.find((t) => t.key === active).node}
        </motion.div>
      </div>
    </section>
  );
};

/* ============ 5. WHY PARTECON ============ */
const WhySection = () => (
  <section className="canvas py-20 md:py-32" data-testid="why-section">
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="text-center max-w-2xl mx-auto">
        <span className="overline">Why PARTECON</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-[#1A1D26]">A different category of software.</h2>
        <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5">Traditional systems record and report. PARTECON operates and coordinates the school in real time.</p>
      </Reveal>
      <div className="mt-10 flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-6">
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#9AA1AD] mr-1">ERP</span>
          {["Record", "Store", "Report"].map((v, i) => (
            <span key={v} className="flex items-center gap-2">
              <span className="rounded-full border border-[#E4E1D8] bg-white/60 px-3 py-1.5 text-[12px] text-[#6B7280]">{v}</span>
              {i < 2 && <span className="text-[#C7C7C0] text-xs">→</span>}
            </span>
          ))}
        </div>
        <span className="hidden lg:block h-8 w-px bg-[#E4E1D8]" />
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#4F46E5] mr-1">PARTECON</span>
          {["Connect", "Operate", "Observe", "Analyse", "Act", "Automate", "Intelligence"].map((v, i, a) => (
            <span key={v} className="flex items-center gap-2">
              <span className="rounded-full bg-[#EEF0FB] px-3 py-1.5 text-[12px] text-[#4F46E5] font-medium">{v}</span>
              {i < a.length - 1 && <span className="text-[#A5B4FC] text-xs">→</span>}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-12"><ComparisonTable /></div>
    </div>
  </section>
);

/* ============ 6. BUILT FOR EVERYONE ============ */
const stakeholders = [
  { icon: Crown, role: "For Principals", key: "principal", d: "Real-time visibility across academics, operations and communication — with actions surfaced, not buried.", tone: "#6366F1" },
  { icon: Building2, role: "For School Administrators", key: "admin", d: "Run institutional operations, provisioning and governance from one connected system.", tone: "#5B7CFA" },
  { icon: Users, role: "For Teachers", key: "teacher", d: "A focused workspace for classroom execution, records and governed communication.", tone: "#22C55E" },
  { icon: GraduationCap, role: "For Students", key: "student", d: "A connected experience for timetable, tasks, assessments and progress.", tone: "#F5B93C" },
  { icon: HomeIcon, role: "For Parents", key: "parent", d: "Stay connected to attendance, academics and school communication — in one place.", tone: "#8B5CF6" },
];

const BuiltForEveryone = () => (
  <section className="canvas pb-20 md:pb-32" data-testid="stakeholders-section">
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <span className="overline">The Role Experiences</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-[#1A1D26]">Five experiences. One operating layer.</h2>
        <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5">
          Every role gets its own tuned experience — but every experience runs on the same PARTECON
          operating layer. It's not five products. It's one system, seen from five angles.
        </p>
      </Reveal>
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {stakeholders.map((s) => (
          <StaggerItem key={s.role}>
            <Link to={`/solutions/${s.key}`} data-testid={`stakeholder-card-${s.key}`}
              className="block rounded-2xl bg-white border border-[#ECECE8] p-6 h-full card-hover">
              <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${s.tone}18` }}>
                <s.icon size={20} style={{ color: s.tone }} />
              </span>
              <h3 className="font-display font-semibold text-[#1A1D26] mt-5 text-lg">{s.role}</h3>
              <p className="text-[#6B7280] text-sm mt-2 leading-relaxed">{s.d}</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-semibold text-[#4F46E5]">See the solution <ArrowUpRight size={13} /></span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

/* ============ 7. FINAL CTA ============ */
const FinalCTA = () => (
  <section className="chrome py-24 md:py-32 relative overflow-hidden" data-testid="final-cta">
    <div className="absolute inset-0 radial-glow" />
    <div className="absolute inset-0 grid-lines opacity-40" />
    <div className="orb orb-violet animate-drift -top-32 left-1/3 h-[440px] w-[440px]" />
    <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <div className="flex justify-center mb-6">
          <LaunchingSoon tone="dark" label="Launching Soon · Pilot cohort forming" />
        </div>
        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-white">
          Ready to operate your school differently?
        </h2>
        <p className="text-white/60 text-lg mt-6 max-w-xl mx-auto">
          Move from fragmented tools to one live operating system. See PARTECON in action.
        </p>
        <div className="flex flex-col sm:flex-row gap-3.5 justify-center mt-10">
          <DemoDialog>
            <button data-testid="cta-demo-btn" className="btn-primary px-8 py-4 text-sm inline-flex items-center justify-center gap-2">Request a PARTECON Demo <ArrowUpRight size={16} /></button>
          </DemoDialog>
          <Link to="/platform" data-testid="cta-contact-btn" className="btn-ghost px-8 py-4 text-sm inline-flex items-center justify-center gap-2">Explore the Platform <ArrowUpRight size={16} /></Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <ProblemThesis />
      <PillarsHome />
      <BuiltForEveryone />
      <ProductExperience />
      <WhySection />
      <FinalCTA />
    </>
  );
}
